import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { defectService, Defect } from '../services/defectService';
import { groupService, Group } from '../services/groupService';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import StatsBar from '../components/StatsBar';
import FilterBar from '../components/FilterBar';
import DefectsTable from '../components/DefectsTable';
import Pagination from '../components/Pagination';
import '../styles/Dashboard.css';

const HISTORY_SNAPSHOT_CACHE_TTL_MS = 2 * 60 * 1000;
const GROUP_OPTIONS_CACHE_TTL_MS = 15 * 60 * 1000;
const HISTORY_SNAPSHOT_CACHE_PREFIX = 'qualisu:history-page:snapshot:';
const HISTORY_GROUPS_CACHE_KEY = 'qualisu:history-page:groups:v1';
const EXACT_HISTORY_POLL_DELAY_MS = 1200;
const MAX_EXACT_HISTORY_POLLS = 6;

interface CachedValue<T> {
  cachedAt: number;
  value: T;
}

interface HistorySnapshot {
  defects: Defect[];
  pagination: {
    currentPage: number;
    pageSize: number;
    totalRecords: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    isApproximate?: boolean;
  };
  validationStats: {
    totalValidatedTrue: number;
    totalValidatedFalse: number;
    isApproximate?: boolean;
  };
}

interface GroupOptionsSnapshot {
  parentGroups: Group[];
  groups: Group[];
}

interface HistoryCacheKeyParams {
  page: number;
  pageSize: number;
  selectedParentGroup: string;
  selectedGroup: string;
  selectedDate: string;
  searchTerm: string;
  groupByDescription: boolean;
}

const DEFAULT_HISTORY_QUERY: HistoryCacheKeyParams = {
  page: 1,
  pageSize: 50,
  selectedParentGroup: '',
  selectedGroup: '',
  selectedDate: '',
  searchTerm: '',
  groupByDescription: false,
};

function readCachedValue<T>(key: string, maxAgeMs: number): T | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(key);
    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue) as CachedValue<T>;
    if (!parsed || typeof parsed.cachedAt !== 'number') {
      window.localStorage.removeItem(key);
      return null;
    }

    if (Date.now() - parsed.cachedAt > maxAgeMs) {
      window.localStorage.removeItem(key);
      return null;
    }

    return parsed.value;
  } catch {
    return null;
  }
}

function writeCachedValue<T>(key: string, value: T) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    const payload: CachedValue<T> = {
      cachedAt: Date.now(),
      value,
    };

    window.localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    // localStorage doluysa veya kapalıysa sessizce devam et.
  }
}

function buildHistoryCacheKey(cacheScope: string, params: HistoryCacheKeyParams) {
  return `${HISTORY_SNAPSHOT_CACHE_PREFIX}${cacheScope}:${JSON.stringify(params)}`;
}

function getDefaultHistorySnapshot(cacheScope: string) {
  return readCachedValue<HistorySnapshot>(
    buildHistoryCacheKey(cacheScope, DEFAULT_HISTORY_QUERY),
    HISTORY_SNAPSHOT_CACHE_TTL_MS
  );
}

function getCachedGroupOptions() {
  return readCachedValue<GroupOptionsSnapshot>(HISTORY_GROUPS_CACHE_KEY, GROUP_OPTIONS_CACHE_TTL_MS);
}

function snapshotHasApproximateTotals(snapshot: HistorySnapshot | null | undefined) {
  return Boolean(snapshot?.pagination.isApproximate || snapshot?.validationStats.isApproximate);
}

const HistoryPage: React.FC = () => {
  const { user } = useAuth();
  const historyCacheScope = user?.username || 'anonymous';
  const [initialHistorySnapshot] = useState<HistorySnapshot | null>(() => getDefaultHistorySnapshot(historyCacheScope));
  const [initialGroupOptions] = useState<GroupOptionsSnapshot | null>(() => getCachedGroupOptions());
  const exactHistoryRefreshTimerRef = useRef<number | null>(null);

  const [defects, setDefects] = useState<Defect[]>(() => initialHistorySnapshot?.defects || []);
  const [filteredDefects, setFilteredDefects] = useState<Defect[]>(() => initialHistorySnapshot?.defects || []);
  const [parentGroups, setParentGroups] = useState<Group[]>(() => initialGroupOptions?.parentGroups || []);
  const [groups, setGroups] = useState<Group[]>(() => initialGroupOptions?.groups || []);
  const [isLoading, setIsLoading] = useState(!initialHistorySnapshot);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [isHistoryApproximate, setIsHistoryApproximate] = useState(() => snapshotHasApproximateTotals(initialHistorySnapshot));
  const [isRefreshingHistoryMetrics, setIsRefreshingHistoryMetrics] = useState(false);

  // Pagination state (backend-driven)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalRecords, setTotalRecords] = useState(() => initialHistorySnapshot?.pagination.totalRecords || 0);
  const [totalPages, setTotalPages] = useState(() => initialHistorySnapshot?.pagination.totalPages || 0);
  const [hasNextPage, setHasNextPage] = useState(() => initialHistorySnapshot?.pagination.hasNextPage || false);
  const [hasPrevPage, setHasPrevPage] = useState(() => initialHistorySnapshot?.pagination.hasPrevPage || false);

  // Validation stats
  const [totalValidatedTrue, setTotalValidatedTrue] = useState(
    () => initialHistorySnapshot?.validationStats.totalValidatedTrue || 0
  );
  const [totalValidatedFalse, setTotalValidatedFalse] = useState(
    () => initialHistorySnapshot?.validationStats.totalValidatedFalse || 0
  );

  // Filter states
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedParentGroup, setSelectedParentGroup] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [groupByDescription, setGroupByDescription] = useState(false);

  // Sorting
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const historyLimit = groupByDescription ? 200 : pageSize;
  const hasVisibleRows = filteredDefects.length > 0 || totalRecords > 0;
  const canRenderResults = !error && (!isLoading || hasVisibleRows);
  const isHistorySummaryPending = isHistoryApproximate || isRefreshingHistoryMetrics;
  const historyStatusNote = isHistorySummaryPending
    ? 'Toplam sayılar arka planda netleştiriliyor.'
    : '';
  const totalRecordsLabel = isHistoryApproximate && hasNextPage
    ? `${totalRecords.toLocaleString()}+`
    : totalRecords.toLocaleString();

  const historyCacheKey = useMemo(
    () => buildHistoryCacheKey(historyCacheScope, {
      page: currentPage,
      pageSize: historyLimit,
      selectedParentGroup,
      selectedGroup,
      selectedDate,
      searchTerm,
      groupByDescription,
    }),
    [historyCacheScope, currentPage, historyLimit, selectedParentGroup, selectedGroup, selectedDate, searchTerm, groupByDescription]
  );

  const clearExactHistoryRefreshTimer = useCallback(() => {
    if (exactHistoryRefreshTimerRef.current !== null) {
      window.clearTimeout(exactHistoryRefreshTimerRef.current);
      exactHistoryRefreshTimerRef.current = null;
    }
  }, []);

  const applyHistorySnapshot = useCallback((snapshot: HistorySnapshot) => {
    setDefects(snapshot.defects);
    setTotalRecords(snapshot.pagination.totalRecords);
    setTotalPages(snapshot.pagination.totalPages);
    setHasNextPage(snapshot.pagination.hasNextPage);
    setHasPrevPage(snapshot.pagination.hasPrevPage);
    setTotalValidatedTrue(snapshot.validationStats.totalValidatedTrue);
    setTotalValidatedFalse(snapshot.validationStats.totalValidatedFalse);
    setIsHistoryApproximate(snapshotHasApproximateTotals(snapshot));
  }, []);

  const loadGroups = useCallback(async ({ forceRefresh = false }: { forceRefresh?: boolean } = {}) => {
    const cachedOptions = forceRefresh ? null : getCachedGroupOptions();

    if (cachedOptions) {
      setParentGroups(cachedOptions.parentGroups);
      setGroups(cachedOptions.groups);
    }

    const [parentGroupsResult, groupsResult] = await Promise.allSettled([
      groupService.getParentGroups(),
      groupService.getGroups(),
    ]);

    let resolvedParentGroups: Group[] | null = cachedOptions?.parentGroups || null;
    let resolvedGroups: Group[] | null = cachedOptions?.groups || null;
    const warnings: string[] = [];

    if (parentGroupsResult.status === 'fulfilled' && parentGroupsResult.value.success) {
      resolvedParentGroups = parentGroupsResult.value.data;
      setParentGroups(parentGroupsResult.value.data);
    } else if (!cachedOptions?.parentGroups?.length) {
      warnings.push('Üst grup filtreleri şu anda yüklenemedi.');
    }

    if (groupsResult.status === 'fulfilled' && groupsResult.value.success) {
      resolvedGroups = groupsResult.value.data;
      setGroups(groupsResult.value.data);
    } else if (!cachedOptions?.groups?.length) {
      warnings.push('Grup filtreleri şu anda yüklenemedi.');
    }

    if (resolvedParentGroups !== null && resolvedGroups !== null) {
      writeCachedValue(HISTORY_GROUPS_CACHE_KEY, {
        parentGroups: resolvedParentGroups,
        groups: resolvedGroups,
      });
    }

    if (warnings.length > 0) {
      setWarning((prev) => prev || warnings.join(' '));
    }
  }, []);

  const loadHistoryData = useCallback(async ({
    forceRefresh = false,
    silent = false,
  }: {
    forceRefresh?: boolean;
    silent?: boolean;
  } = {}) => {
    if (!silent) {
      setIsLoading(true);
      setError('');
    }

    const cachedSnapshot = forceRefresh
      ? null
      : readCachedValue<HistorySnapshot>(historyCacheKey, HISTORY_SNAPSHOT_CACHE_TTL_MS);

    if (cachedSnapshot && !silent) {
      applyHistorySnapshot(cachedSnapshot);
    }

    try {
      const trimmedSearch = searchTerm.trim();
      const defectsRes = await defectService.getHistory({
        page: currentPage,
        limit: historyLimit,
        sortBy: groupByDescription ? 'description' : 'generatedAt',
        sortOrder: groupByDescription ? 'asc' : 'desc',
        ...(selectedParentGroup && { parentControlItemGroupName: selectedParentGroup }),
        ...(selectedGroup && { controlItemGroupName: selectedGroup }),
        ...(selectedDate && { generatedAtDate: selectedDate }),
        ...(trimmedSearch && { description: trimmedSearch }),
      });

      if (!defectsRes.success) {
        throw new Error('Hata geçmişi yüklenemedi');
      }

      const nextSnapshot: HistorySnapshot = {
        defects: defectsRes.data || [],
        pagination: {
          currentPage: defectsRes.pagination?.currentPage ?? currentPage,
          pageSize: defectsRes.pagination?.pageSize ?? historyLimit,
          totalRecords: defectsRes.pagination?.totalRecords ?? 0,
          totalPages: defectsRes.pagination?.totalPages ?? 0,
          hasNextPage: defectsRes.pagination?.hasNextPage ?? false,
          hasPrevPage: defectsRes.pagination?.hasPrevPage ?? currentPage > 1,
          isApproximate: defectsRes.pagination?.isApproximate ?? false,
        },
        validationStats: {
          totalValidatedTrue: defectsRes.validationStats?.totalValidatedTrue ?? 0,
          totalValidatedFalse: defectsRes.validationStats?.totalValidatedFalse ?? 0,
          isApproximate: defectsRes.validationStats?.isApproximate ?? false,
        },
      };

      applyHistorySnapshot(nextSnapshot);
      writeCachedValue(historyCacheKey, nextSnapshot);
      if (!silent) {
        setWarning('');
      }

      return {
        success: true,
        isApproximate: snapshotHasApproximateTotals(nextSnapshot),
      };
    } catch (err: any) {
      if (cachedSnapshot) {
        applyHistorySnapshot(cachedSnapshot);
        if (!silent) {
          setWarning('Canlı history verisi alınamadı; son başarılı kayıtlar gösteriliyor.');
        }
        return {
          success: false,
          isApproximate: snapshotHasApproximateTotals(cachedSnapshot),
        };
      }

      setError(err.response?.data?.message || err.message || 'Veriler yüklenirken hata oluştu');
      return {
        success: false,
        isApproximate: false,
      };
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, [
    applyHistorySnapshot,
    currentPage,
    groupByDescription,
    historyCacheKey,
    historyLimit,
    searchTerm,
    selectedDate,
    selectedGroup,
    selectedParentGroup,
  ]);

  const pollForExactHistoryMetrics = useCallback((attempt = 0) => {
    clearExactHistoryRefreshTimer();

    if (attempt >= MAX_EXACT_HISTORY_POLLS) {
      setIsRefreshingHistoryMetrics(false);
      return;
    }

    exactHistoryRefreshTimerRef.current = window.setTimeout(async () => {
      setIsRefreshingHistoryMetrics(true);
      const result = await loadHistoryData({ forceRefresh: true, silent: true });

      if (result.success && result.isApproximate) {
        pollForExactHistoryMetrics(attempt + 1);
        return;
      }

      clearExactHistoryRefreshTimer();
      setIsRefreshingHistoryMetrics(false);
    }, EXACT_HISTORY_POLL_DELAY_MS);
  }, [clearExactHistoryRefreshTimer, loadHistoryData]);

  const handleRefresh = useCallback(async () => {
    clearExactHistoryRefreshTimer();
    setIsRefreshingHistoryMetrics(false);
    setWarning('');
    await Promise.allSettled([
      loadHistoryData({ forceRefresh: true }),
      loadGroups({ forceRefresh: true }),
    ]);
  }, [clearExactHistoryRefreshTimer, loadGroups, loadHistoryData]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    loadHistoryData();
  }, [loadHistoryData]);

  useEffect(() => {
    const debounceId = window.setTimeout(() => {
      setSearchTerm((currentValue) => (currentValue === searchInput ? currentValue : searchInput));
    }, 350);

    return () => window.clearTimeout(debounceId);
  }, [searchInput]);

  useEffect(() => {
    clearExactHistoryRefreshTimer();

    if (isLoading || error || !isHistoryApproximate) {
      setIsRefreshingHistoryMetrics(false);
      return;
    }

    pollForExactHistoryMetrics(0);

    return () => {
      clearExactHistoryRefreshTimer();
    };
  }, [
    clearExactHistoryRefreshTimer,
    error,
    historyCacheKey,
    isHistoryApproximate,
    isLoading,
    pollForExactHistoryMetrics,
  ]);

  useEffect(() => () => {
    clearExactHistoryRefreshTimer();
  }, [clearExactHistoryRefreshTimer]);

  // Client-side filtreleme artık gerekli değil, backend'de yapılıyor
  // Ancak sıralama için filteredDefects'i defects'e eşitliyoruz
  useEffect(() => {
    setFilteredDefects(defects);
  }, [defects]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    setCurrentPage(1);
  };

  const handleParentGroupChange = (value: string) => {
    setSelectedParentGroup(value);
    setCurrentPage(1);
  };

  const handleGroupChange = (value: string) => {
    setSelectedGroup(value);
    setCurrentPage(1);
  };

  const handleDateChange = (value: string) => {
    setSelectedDate(value);
    setCurrentPage(1);
  };

  const handleGroupByDescriptionChange = (value: boolean) => {
    setGroupByDescription(value);
    setCurrentPage(1);
  };

  const handleValidationChange = async (defectId: string, newValidation: boolean) => {
    try {
      const response = await defectService.updateValidation(defectId, newValidation);

      if (response.success) {
        await loadHistoryData({ forceRefresh: true });
        console.log(`✅ Doğrulama güncellendi: ${defectId} → ${newValidation}`);
      }
    } catch (error: any) {
      console.error('❌ Doğrulama güncellenirken hata:', error);
      alert('Doğrulama güncellenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleBulkValidationChange = async (description: string, validation: boolean) => {
    if (!window.confirm(
      `Bu açıklamaya sahip tüm kayıtları ${validation ? 'onaylamak' : 'reddetmek'} istediğinizden emin misiniz?`
    )) {
      return;
    }

    try {
      const response = await defectService.bulkValidateByDescription(description, validation);

      if (response.success) {
        alert(`${response.updatedCount} kayıt başarıyla güncellendi`);
        await loadHistoryData({ forceRefresh: true });
        console.log(`✅ Toplu doğrulama: "${description.substring(0, 50)}..." → ${response.updatedCount} kayıt güncellendi`);
      }
    } catch (error: any) {
      console.error('❌ Toplu doğrulama güncellenirken hata:', error);
      alert('Toplu doğrulama güncellenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleSuggestionsUpdate = async (defectId: string, suggestions: {
    generatedGroup?: string;
    generatedParentGroup?: string;
    generatedTag?: string;
  }) => {
    try {
      const response = await defectService.updateSuggestions(defectId, suggestions);

      if (response.success) {
        setDefects((prevDefects) =>
          prevDefects.map((defect) =>
            (defect._id || defect.id) === defectId
              ? {
                  ...defect,
                  generatedGroup:
                    suggestions.generatedGroup !== undefined
                      ? suggestions.generatedGroup
                      : (defect as any).generatedGroup,
                  generatedParentGroup:
                    suggestions.generatedParentGroup !== undefined
                      ? suggestions.generatedParentGroup
                      : (defect as any).generatedParentGroup,
                  generatedTag:
                    suggestions.generatedTag !== undefined
                      ? suggestions.generatedTag
                      : (defect as any).generatedTag,
                }
              : defect
          )
        );

        console.log(`✅ AI önerileri güncellendi: ${defectId}`);
      }
    } catch (error: any) {
      console.error('❌ Öneriler güncellenirken hata:', error);
      alert('Öneriler güncellenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleBulkSuggestionsUpdate = async (description: string, suggestions: {
    generatedGroup?: string;
    generatedParentGroup?: string;
    generatedTag?: string;
  }) => {
    try {
      const response = await defectService.bulkUpdateSuggestions(description, suggestions);

      if (response.success) {
        await loadHistoryData({ forceRefresh: true });
        alert(`${response.updatedCount} kayıt başarıyla güncellendi`);
        console.log(`✅ Toplu AI önerileri güncellendi: "${description.substring(0, 50)}..." → ${response.updatedCount} kayıt`);
      }
    } catch (error: any) {
      console.error('❌ Toplu öneriler güncellenirken hata:', error);
      alert('Toplu öneriler güncellenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleDelete = async (defectId: string) => {
    if (!window.confirm('Bu hatayı silmek istediğinizden emin misiniz?')) {
      return;
    }

    try {
      const response = await defectService.delete(defectId);

      if (response.success) {
        await loadHistoryData({ forceRefresh: true });
        console.log(`🗑️ Hata silindi: ${defectId}`);
        alert('Hata başarıyla silindi');
      }
    } catch (error: any) {
      console.error('❌ Hata silinirken hata:', error);
      if (error.response?.status === 403) {
        alert('Bu işlem için admin yetkisi gereklidir');
      } else {
        alert('Hata silinirken bir hata oluştu: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const sortedDefects = useMemo(() => {
    if (!sortColumn) return filteredDefects;

    return [...filteredDefects].sort((a: any, b: any) => {
      const aVal = a[sortColumn] || '';
      const bVal = b[sortColumn] || '';

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      }

      return aVal < bVal ? 1 : -1;
    });
  }, [filteredDefects, sortColumn, sortDirection]);

  // Backend pagination kullanıyoruz - paginatedDefects = sortedDefects
  const paginatedDefects = sortedDefects;

  const exportToCSV = () => {
    const headers = [
      'Açıklama',
      'Grup',
      'Üst Grup',
      'Önerilen Grup',
      'Önerilen Üst Grup',
      'Önerilen Etiket',
      'Önerilen Çözüm',
      'Oluşturulma Tarihi',
      'Doğrulama'
    ];
    const rows = filteredDefects.map((defect: any) => [
      defect.description || '',
      defect.controlItemGroupName || '',
      defect.parentControlItemGroupName || '',
      defect.generatedGroup || '',
      defect.generatedParentGroup || '',
      defect.generatedTag || '',
      defect.generatedSolution || '',
      defect.generatedAt || '',
      defect.validation ? 'Evet' : 'Hayır',
    ]);

    let csvContent = headers.join(',') + '\n';
    rows.forEach((row) => {
      csvContent += row.map((cell) => `"${cell}"`).join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `qualisu-hata-gecmisi-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="container">
      <Navigation />

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '20px'
      }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#333', margin: 0 }}>🤖 Yapay Zeka Hata Geçmişi</h1>
          <p style={{ color: '#666', marginTop: '5px', margin: 0 }}>
            AI tarafından oluşturulan hata kayıtları
          </p>
          {historyStatusNote && (
            <p style={{ color: '#8a6400', marginTop: '6px', marginBottom: 0, fontSize: '13px' }}>
              {historyStatusNote}
            </p>
          )}
        </div>
        <div style={{
          flexShrink: 0,
          transform: 'scale(0.85)',
          transformOrigin: 'right center'
        }}>
          <StatsBar
            defects={filteredDefects}
            totalRecords={totalRecords}
            totalValidatedTrue={totalValidatedTrue}
            totalValidatedFalse={totalValidatedFalse}
            totalsAreApproximate={isHistoryApproximate}
            hasMoreRecords={hasNextPage}
          />
        </div>
      </div>

      <FilterBar
        searchTerm={searchInput}
        onSearchChange={handleSearchChange}
        selectedParentGroup={selectedParentGroup}
        onParentGroupChange={handleParentGroupChange}
        selectedGroup={selectedGroup}
        onGroupChange={handleGroupChange}
        selectedDate={selectedDate}
        onDateChange={handleDateChange}
        parentGroups={parentGroups}
        groups={groups}
        onRefresh={handleRefresh}
        onExport={exportToCSV}
        groupByDescription={groupByDescription}
        onGroupByDescriptionChange={handleGroupByDescriptionChange}
      />

      {isLoading && (
        <div className="loading">
          <div className="spinner"></div>
          <p>Hata geçmişi yükleniyor... (Sayfa {currentPage}/{Math.max(totalPages, 1)})</p>
        </div>
      )}

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
          <button className="close-btn" onClick={() => setError('')}>
            ✕
          </button>
        </div>
      )}

      {warning && !error && (
        <div
          className="error-message"
          style={{ background: '#fff8dc', borderColor: '#f0d28c', color: '#7a5200' }}
        >
          <span className="error-icon">ℹ️</span>
          <span>{warning}</span>
          <button className="close-btn" onClick={() => setWarning('')}>
            ✕
          </button>
        </div>
      )}

      {canRenderResults && (
        <>
          <DefectsTable
            defects={paginatedDefects}
            onSort={handleSort}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onValidationChange={handleValidationChange}
            onBulkValidationChange={handleBulkValidationChange}
            onSuggestionsUpdate={handleSuggestionsUpdate}
            onBulkSuggestionsUpdate={handleBulkSuggestionsUpdate}
            onDelete={handleDelete}
            groups={groups}
            parentGroups={parentGroups.map(g => g.name)}
            userRole={user?.role}
            groupByDescription={groupByDescription}
          />

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={totalRecords}
            hasNextPage={hasNextPage}
            hasPrevPage={hasPrevPage}
            isApproximate={isHistoryApproximate}
            onPageChange={setCurrentPage}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setCurrentPage(1);
            }}
          />

          <footer>
            <p>© 2025 Qualisu Paneli | Toplam Kayıt: {totalRecordsLabel} | Gösterilen: {filteredDefects.length}</p>
          </footer>
        </>
      )}
    </div>
  );
};

export default HistoryPage;
